import React, { useEffect, useState } from "react";
import api from "../../api";

// ===== TipTap (READ-ONLY RENDER) =====
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import MathExtension from "./MathExtension";
import CustomImageExtension from "./CustomImageExtension";

// ===== ReactQuill (EDIT BEFORE REJECT) =====
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../../css/quill-fonts.css";

// ----- Quill Fonts -----
const Font = Quill.import("formats/font");
Font.whitelist = [
  "arial",
  "times-new-roman",
  "georgia",
  "courier-new",
  "verdana",
];
Quill.register(Font, true);

// ----- Quill Toolbar -----
const toolbarOptions = [
  [{ font: Font.whitelist }],
  [{ size: ["small", false, "large", "huge"] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ["blockquote", "code-block"],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ script: "sub" }, { script: "super" }],
  ["link", "image", "video"],
  ["clean"],
];

const modules = { toolbar: toolbarOptions };

const formats = [
  "font",
  "size",
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "blockquote",
  "code-block",
  "list",
  "bullet",
  "script",
  "link",
  "image",
  "video",
];

// =====================================================
// 🧠 SAFE CONTENT RENDERER (JSON OR HTML)
// =====================================================
const ReadOnlyBlogContent = ({ content }) => {
  // 🧠 detect JSON safely (NO hooks here)
  let parsed = null;
  let isJson = false;

  try {
    parsed = JSON.parse(content);
    isJson = parsed?.type === "doc";
  } catch {
    isJson = false;
  }

  // ✅ Hook is ALWAYS called (rules satisfied)
  const editor = useEditor({
    extensions: [StarterKit, MathExtension, CustomImageExtension],
    editable: false,
    content: isJson ? parsed : "", // empty when HTML
  });

  // ✅ Update editor only when JSON changes
  useEffect(() => {
    if (editor && isJson) {
      editor.commands.setContent(parsed);
    }
  }, [editor, isJson, parsed]);

  // ✅ CASE 1: TipTap JSON
  if (isJson) {
    if (!editor) return null;

    return (
      <EditorContent
        editor={editor}
        className="tiptap-editor border rounded p-3"
      />
    );
  }

  // ✅ CASE 2: HTML (ReactQuill / legacy)
  return (
    <div
      className="border rounded p-3"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};


// =====================================================
// MAIN COMPONENT
// =====================================================
const AdminBlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [rejectBlog, setRejectBlog] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");

  const token = localStorage.getItem("token");

  // ----- Fetch Blogs -----
  const fetchBlogs = async () => {
    try {
      const res = await api.get("/api/blog/getall", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlogs(res.data);
    } catch (err) {
      console.error("Error fetching blogs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // ----- Update Status -----
  const updateStatus = async (id, status) => {
    try {
      await api.put(
        `/api/blog/admins/updatestatus/${id}`,
        {},
        {
          params: { status },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchBlogs();
    } catch {
      alert("Failed to update status");
    }
  };
const extractTextFromTipTapJSON = (jsonString) => {
  try {
    const json =
      typeof jsonString === "string"
        ? JSON.parse(jsonString)
        : jsonString;

    let text = "";

    const traverse = (node) => {
      if (!node) return;

      if (node.type === "text" && node.text) {
        text += node.text + " ";
      }

      if (node.content) {
        node.content.forEach(traverse);
      }
    };

    traverse(json);
    return text.trim();
  } catch (e) {
    return "";
  }
};
  // ----- Reject Flow -----
 const handleRejectClick = (blog) => {
  setRejectBlog(blog);
  setEditTitle(blog.title);

  try {
    const parsed = JSON.parse(blog.textContent);

    if (parsed?.type === "doc") {
      setEditContent(
        extractTextFromTipTapJSON(blog.textContent)
      );
    } else {
      setEditContent(blog.textContent);
    }
  } catch {
    setEditContent(blog.textContent);
  }
};

  const saveAndReject = async () => {
    try {
      await api.put(
        `/api/blog/text/${rejectBlog.id}`,
        { title: editTitle, textContent: editContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await api.put(
        `/api/blog/admins/updatestatus/${rejectBlog.id}`,
        {},
        {
          params: { status: "REJECTED" },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("Blog rejected successfully");
      setRejectBlog(null);
      fetchBlogs();
    } catch {
      alert("Failed to reject blog");
    }
  };

  if (loading) return <p>Loading blogs...</p>;

  return (
    <div className="container py-4">
      <h3 className="mb-4">📑 All Blogs (Admin Panel)</h3>

      {/* ===== Reject Modal ===== */}
      {rejectBlog && (
        <div className="modal show d-block" style={{ background: "#00000055" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content p-3">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  ✏️ Edit Before Reject
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setRejectBlog(null)}
                />
              </div>

              <div className="modal-body">
                <label className="fw-bold">Title</label>
                <input
                  className="form-control mb-3"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />

                <label className="fw-bold">Content</label>
                <ReactQuill
                  theme="snow"
                  value={editContent}
                  onChange={setEditContent}
                  modules={modules}
                  formats={formats}
                  style={{ height: "250px", marginBottom: "60px" }}
                />
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setRejectBlog(null)}
                >
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={saveAndReject}>
                  ❌ Save & Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Blog List ===== */}
      {blogs.length > 0 ? (
        blogs.map((blog) => (
          <div key={blog.id} className="card mb-3 shadow-sm p-3">
            <h5>
              #{blog.id} - {blog.title}
            </h5>

            {/* ✅ SAFE CONTENT RENDER */}
            <ReadOnlyBlogContent content={blog.textContent} />

            <p className="mt-2">
              <strong>Posted By:</strong> {blog.postedByEmail}
            </p>

            <p>
              <strong>Status:</strong>{" "}
              <span
                className={`badge ${
                  blog.status === "APPROVED"
                    ? "bg-success"
                    : blog.status === "REJECTED"
                    ? "bg-danger"
                    : "bg-warning text-dark"
                }`}
              >
                {blog.status}
              </span>
            </p>

            <div className="d-flex gap-2">
              {blog.status !== "REJECTED" && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => updateStatus(blog.id, "APPROVED")}
                  >
                    ✅ Approve
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleRejectClick(blog)}
                  >
                    ❌ Reject
                  </button>
                </>
              )}

              {blog.status === "REJECTED" && (
                <button
                  className="btn btn-warning"
                  onClick={() => handleRejectClick(blog)}
                >
                  💬 Reply
                </button>
              )}
            </div>
          </div>
        ))
      ) : (
        <p>No blogs found.</p>
      )}
    </div>
  );
};

export default AdminBlogList;
